/**
 * Demo Mode Utilities
 * Detects if app is running in demo mode and provides demo-specific functionality
 */

// Detect demo mode
export const isDemoMode = (): boolean => {
    // Check for demo mode indicators
    if (typeof window !== 'undefined') {
        // In Electron (desktop app) or Android (Capacitor native app), check if explicitly running in demo mode
        const isElectron = /Electron/.test(navigator.userAgent);
        const isNative = typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.isNativePlatform();
        
        // If Electron or Android native app, disable demo mode unless explicitly requested
        if (isElectron || isNative) {
            // Only enable demo mode in native apps if explicitly requested via URL or config
            if (window.location.search.includes('demo=true')) return true;
            if ((window as any).RegIQDemo?.config?.demoMode) return true;
            // Otherwise, native apps (Electron/Android) are full version - no demo mode
            return false;
        }
        
        // For web/browser version only, check demo indicators
        // Check URL parameters
        if (window.location.search.includes('demo=true')) return true;
        
        // Check if explicitly running in demo path (not just localhost)
        if (window.location.pathname.includes('/demo')) return true;
        
        // Check for demo config
        if ((window as any).RegIQDemo?.config?.demoMode) return true;
        
        // Check for WordPress demo indicator (only for web)
        if ((window as any).regiqData) return true;
    }
    return false;
};

// Demo license info (bypasses all license checks)
export const getDemoLicense = () => {
    return {
        tier: 'PROFESSIONAL' as const,
        expiryDate: Date.now() + 365 * 24 * 60 * 60 * 1000, // 1 year from now
        contactName: 'Demo User',
        companyName: 'Demo Account',
        key: 'DEMO-MODE-KEY',
        hwId: 'demo-hardware-id'
    };
};

// Watermark export via WordPress API
export const watermarkExport = async (data: string, format: 'png' | 'pdf' = 'png'): Promise<string> => {
    if (!isDemoMode()) return data;
    
    const apiUrl = (window as any).regiqData?.apiUrl || '/wp-json/regiq/v1';
    
    try {
        const response = await fetch(`${apiUrl}/watermark`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                image: data,
                format: format
            })
        });
        
        if (!response.ok) {
            throw new Error('Watermarking failed');
        }
        
        const result = await response.json();
        return result.image || data; // Return watermarked image
    } catch (error) {
        console.error('Watermarking error:', error);
        // In demo mode, show warning but still allow export
        alert('Demo Mode: This export would be watermarked in the full version.\n\nAll exports in demo mode include the RegIQ logo watermark.');
        return data;
    }
};

// Block export in demo mode (shows message instead)
export const blockExportInDemo = (): boolean => {
    if (!isDemoMode()) return false;
    
    alert('Demo Mode: Export functionality is disabled.\n\nIn the full version, you can export high-quality PDFs and images.\n\nVisit the pricing page to get the full version!');
    return true; // Export is blocked
};

// Show demo banner message
export const getDemoMessage = (): string => {
    return 'Demo Mode - All exports are watermarked. Get the full version for unlimited exports!';
};
